/* =========================================================================
   use-count-up.ts
   Smooth numeric count-up animation with prefers-reduced-motion support.
   ========================================================================= */

import { useState, useEffect, useRef, useMemo } from "react";

function getPrefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Ease-out quad */
function easeOutQuad(t: number): number {
  return t * (2 - t);
}

export function useCountUp(
  target: number,
  duration = 800,
  options: { decimals?: number } = {},
): number {
  const safeTarget = Number.isFinite(target) ? target : 0;
  const [value, setValue] = useState(0);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number>(0);
  const fromRef = useRef<number>(0);
  const reduced = useMemo(() => getPrefersReducedMotion(), []);

  useEffect(() => {
    if (reduced || typeof window === "undefined") {
      setValue(safeTarget);
      return;
    }

    fromRef.current = value;
    startRef.current = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutQuad(progress);
      const current = fromRef.current + (safeTarget - fromRef.current) * eased;

      setValue(current);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(rafRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeTarget, duration, reduced]);

  return reduced ? safeTarget : Number(value.toFixed(options.decimals ?? 0));
}
